/*
 * Runs the web tracker for the signed-in user: reads the case from the route,
 * feeds input, visibility and focus into `ActivityClock`, and sends segments
 * to `POST /api/time/beats` every minute, on a case change and on `pagehide`.
 */
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { TimeBeatsAPI } from "../api/timeApi";
import type { BeatSegment } from "../types";
import { ActivityClock, contextFromLocation, sameCase } from "./trackerCore";
import { useTrackerStore } from "./trackerStore";

export const TICK_MS = 5_000;
export const FLUSH_MS = 60_000;
const INPUT_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart"
] as const;

export const useWorkTracker = (enabled: boolean): void => {
  const location = useLocation();
  const routeContext = useMemo(
    () => contextFromLocation(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const paused = useTrackerStore((s) => s.paused);
  const override = useTrackerStore((s) => s.override);
  const clockRef = useRef<ActivityClock | null>(null);
  const tokenRef = useRef<string | null>(null);
  const sendingRef = useRef(false);
  const flushRef = useRef<() => void>(() => {});

  if (!clockRef.current) clockRef.current = new ActivityClock();

  // A route on another case drops the chip's "switch case" override.
  const prevRoute = useRef(routeContext);
  useEffect(() => {
    if (!sameCase(prevRoute.current, routeContext)) {
      useTrackerStore.getState().setOverride(null);
    }
    prevRoute.current = routeContext;
  }, [routeContext]);

  const context = override ?? routeContext;

  useEffect(() => {
    const clock = clockRef.current;
    if (!clock) return;
    const caseChanged = !sameCase(clock.current, context);
    clock.setContext(context);
    if (caseChanged) flushRef.current();
  }, [context]);

  useEffect(() => {
    clockRef.current?.setPaused(paused);
  }, [paused]);

  useEffect(() => {
    const clock = clockRef.current;
    if (!enabled || !clock) return undefined;

    const signals = () => ({
      visible: document.visibilityState === "visible",
      focused: document.hasFocus(),
      agentWorking: useTrackerStore.getState().agentTurns > 0
    });
    const publish = () =>
      useTrackerStore.getState().setSnapshot({
        context: clock.current,
        seconds: clock.secondsFor(clock.current),
        counting: clock.isCounting
      });
    const prime = () => {
      void TimeBeatsAPI.primeToken()
        .then((t) => {
          tokenRef.current = t;
        })
        .catch(() => {});
    };

    const flush = () => {
      clock.tick(signals());
      const batch: BeatSegment[] = clock.drain();
      publish();
      prime();
      if (!batch.length || sendingRef.current) {
        if (batch.length) clock.requeue(batch);
        return;
      }
      sendingRef.current = true;
      TimeBeatsAPI.send(batch)
        .then((res) => useTrackerStore.getState().addLabels(res.labels))
        .catch((error: { status?: number }) => {
          // 403: the firm turned time tracking off; drop rather than retry.
          if (error?.status !== 403) clock.requeue(batch);
        })
        .finally(() => {
          sendingRef.current = false;
        });
    };
    flushRef.current = flush;

    let lastMove = 0;
    const onInput = (event: Event) => {
      if (event.type === "pointermove") {
        const now = Date.now();
        if (now - lastMove < 2_000) return;
        lastMove = now;
      }
      clock.input();
    };
    const onVisibility = () => {
      clock.tick(signals());
      if (document.visibilityState === "hidden") flush();
      publish();
    };
    const onPageHide = () => {
      clock.tick(signals());
      TimeBeatsAPI.sendOnUnload(clock.drain(), tokenRef.current);
    };

    clock.input();
    prime();
    INPUT_EVENTS.forEach((name) =>
      window.addEventListener(name, onInput, { passive: true, capture: true })
    );
    window.addEventListener("focus", onVisibility);
    window.addEventListener("blur", onVisibility);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    const tick = window.setInterval(() => {
      clock.tick(signals());
      publish();
    }, TICK_MS);
    const flushTimer = window.setInterval(flush, FLUSH_MS);

    return () => {
      window.clearInterval(tick);
      window.clearInterval(flushTimer);
      INPUT_EVENTS.forEach((name) =>
        window.removeEventListener(name, onInput, { capture: true })
      );
      window.removeEventListener("focus", onVisibility);
      window.removeEventListener("blur", onVisibility);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      flush();
      flushRef.current = () => {};
    };
  }, [enabled]);
};
